import { useMemo } from 'react';

interface MonthlyHeatmapProps {
  /** Monthly returns by year: { 2024: { 1: 0.05, 2: -0.02, ... }, ... } */
  monthlyReturns: Record<number, Record<number, number>>;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Monthly returns heatmap showing returns by month and year.
 * Uses colorblind-safe blue/orange palette.
 */
export default function MonthlyHeatmap({ monthlyReturns }: MonthlyHeatmapProps) {
  const { years, yearlyReturns, maxAbsReturn } = useMemo(() => {
    const years = Object.keys(monthlyReturns).map(Number).sort();

    // Calculate yearly totals
    const yearlyReturns: Record<number, number> = {};
    for (const [year, months] of Object.entries(monthlyReturns)) {
      const yearNum = Number(year);
      let cumulative = 1;
      for (const ret of Object.values(months)) {
        cumulative *= (1 + ret);
      }
      yearlyReturns[yearNum] = cumulative - 1;
    }

    // Find max absolute return for color scaling
    let maxAbs = 0;
    for (const months of Object.values(monthlyReturns)) {
      for (const ret of Object.values(months)) {
        maxAbs = Math.max(maxAbs, Math.abs(ret));
      }
    }

    return { years, yearlyReturns, maxAbsReturn: Math.max(maxAbs, 0.05) };
  }, [monthlyReturns]);

  const getCellOpacity = (value: number | undefined): number => {
    if (value === undefined) return 0.3;
    const intensity = Math.min(Math.abs(value) / maxAbsReturn, 1);
    return 0.15 + intensity * 0.7;
  };

  const getTextColor = (value: number | undefined): string => {
    if (value === undefined) return 'text-gray-600';
    if (Math.abs(value) > maxAbsReturn * 0.5) return 'text-white';
    return value > 0 ? 'text-profit' : 'text-loss';
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <h3 className="font-medium text-white mb-4">Monthly Returns</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="py-2 px-2 text-left text-gray-500 font-medium">Year</th>
              {MONTHS.map(month => (
                <th key={month} className="py-2 px-1 text-center text-gray-500 font-medium w-12">
                  {month}
                </th>
              ))}
              <th className="py-2 px-2 text-right text-gray-500 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {years.map(year => (
              <tr key={year} className="border-t border-gray-800">
                <td className="py-1 px-2 text-gray-400 font-medium">{year}</td>
                {Array.from({ length: 12 }, (_, i) => {
                  const month = i + 1;
                  const value = monthlyReturns[year]?.[month];
                  const formatted = value !== undefined ? `${(value * 100).toFixed(1)}%` : '-';

                  return (
                    <td
                      key={month}
                      className="py-1 px-1 text-center"
                    >
                      <div
                        className={`rounded py-1.5 tabular-nums font-medium ${getTextColor(value)}`}
                        style={{
                          backgroundColor: value !== undefined
                            ? value > 0
                              ? `rgba(0, 90, 181, ${getCellOpacity(value)})`
                              : value < 0
                                ? `rgba(230, 97, 0, ${getCellOpacity(value)})`
                                : 'rgba(75, 85, 99, 0.3)'
                            : 'rgba(31, 41, 55, 0.5)'
                        }}
                      >
                        {formatted}
                      </div>
                    </td>
                  );
                })}
                <td className="py-1 px-2 text-right">
                  <span className={`font-medium tabular-nums ${
                    yearlyReturns[year] > 0 ? 'text-profit' : 'text-loss'
                  }`}>
                    {(yearlyReturns[year] * 100).toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-12 h-3 rounded" style={{ background: 'linear-gradient(to right, rgba(0, 90, 181, 0.15), rgba(0, 90, 181, 0.85))' }}></div>
          <span className="text-gray-400">Profit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-3 rounded" style={{ background: 'linear-gradient(to right, rgba(230, 97, 0, 0.15), rgba(230, 97, 0, 0.85))' }}></div>
          <span className="text-gray-400">Loss</span>
        </div>
      </div>

      {/* Summary stats */}
      <div className="mt-3 grid grid-cols-3 gap-4 text-center text-xs">
        <div>
          <p className="text-gray-500">Best Month</p>
          <p className="text-profit font-medium tabular-nums">
            {(() => {
              let best = -Infinity;
              for (const months of Object.values(monthlyReturns)) {
                for (const ret of Object.values(months)) {
                  best = Math.max(best, ret);
                }
              }
              return `${(best * 100).toFixed(1)}%`;
            })()}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Worst Month</p>
          <p className="text-loss font-medium tabular-nums">
            {(() => {
              let worst = Infinity;
              for (const months of Object.values(monthlyReturns)) {
                for (const ret of Object.values(months)) {
                  worst = Math.min(worst, ret);
                }
              }
              return `${(worst * 100).toFixed(1)}%`;
            })()}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Win Months</p>
          <p className="text-white font-medium">
            {(() => {
              let wins = 0;
              let total = 0;
              for (const months of Object.values(monthlyReturns)) {
                for (const ret of Object.values(months)) {
                  total++;
                  if (ret > 0) wins++;
                }
              }
              return `${wins}/${total} (${((wins / total) * 100).toFixed(0)}%)`;
            })()}
          </p>
        </div>
      </div>
    </div>
  );
}
